import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { showError } from "../lib/errorHandler";
import { 
  Bell, 
  Loader2, 
  Check, 
  Send,
  MessageCircle,
  Settings,
  AlertTriangle,
  Info,
  XCircle,
  RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_CONFIG = {
  info: { label: "Info", color: "bg-blue-500", icon: Info },
  warning: { label: "Aviso", color: "bg-amber-500", icon: AlertTriangle },
  critical: { label: "Crítico", color: "bg-red-500", icon: XCircle },
};

const ALERT_TYPE_LABELS = {
  production_90: "Produção 90%",
  order_started: "Ordem Iniciada",
  order_completed: "Ordem Concluída",
  machine_stopped: "Máquina Parada",
  mold_maintenance: "Manutenção Molde",
  quality_issue: "Problema Qualidade",
  maintenance: "Manutenção",
  shift_end: "Fim de Turno",
  user_clock_in: "Entrada",
  user_clock_out: "Saída",
  weight_deviation: "Desvio de Peso",
  low_stock: "Stock Mínimo",
};

export default function Alerts() {
  const [activeTab, setActiveTab] = useState("alerts");
  const [alerts, setAlerts] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [telegramDialogOpen, setTelegramDialogOpen] = useState(false);
  const [chatId, setChatId] = useState("");
  const [testChatId, setTestChatId] = useState("");

  const fetchData = async () => {
    try {
      const [alertsRes, countRes] = await Promise.all([
        axios.get(`${API}/alerts`),
        axios.get(`${API}/alerts/unread-count`),
      ]);
      setAlerts(alertsRes.data);
      setUnreadCount(countRes.data.count);
      
      // Try to get subscriptions (admin only)
      try {
        const subsRes = await axios.get(`${API}/telegram/subscriptions`);
        setSubscriptions(subsRes.data);
      } catch (e) {
        // Not admin, ignore
      }
    } catch (error) {
      toast.error("Erro ao carregar alertas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Poll for new alerts every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleMarkAsRead = async (alertId) => {
    try {
      await axios.post(`${API}/alerts/${alertId}/read`);
      fetchData();
    } catch (error) {
      toast.error("Erro ao marcar como lido");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      for (const alert of alerts) {
        await axios.post(`${API}/alerts/${alert.id}/read`);
      }
      toast.success("Todos os alertas marcados como lidos");
      fetchData();
    } catch (error) {
      toast.error("Erro ao marcar alertas");
    }
  };

  const handleSubscribeTelegram = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/telegram/subscribe?chat_id=${chatId}`);
      toast.success("Subscrição Telegram ativada! Verifique o seu Telegram.");
      setTelegramDialogOpen(false);
      setChatId("");
      fetchData();
    } catch (error) {
      showError(error, "Erro ao ativar subscrição");
    }
  };

  const handleTestTelegram = async () => {
    if (!testChatId) {
      toast.error("Insira o Chat ID para teste");
      return;
    }
    try {
      await axios.post(`${API}/telegram/test?chat_id=${testChatId}`);
      toast.success("Mensagem de teste enviada!");
    } catch (error) {
      showError(error, "Erro ao enviar teste");
    }
  };

  const handleCheckProduction = async () => {
    try {
      await axios.post(`${API}/alerts/check-production`);
      toast.success("Verificação de produção executada");
      fetchData();
    } catch (error) {
      toast.error("Erro ao verificar produção");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="alerts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Centro de Alertas
          </h1>
          <p className="text-muted-foreground mt-1">
            Notificações e configuração de alertas Telegram
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleCheckProduction}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Verificar Produção
          </Button>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={handleMarkAllAsRead}>
              <Check className="h-4 w-4 mr-2" />
              Marcar Todos Lidos
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Total Alertas</p>
              <p className="text-3xl font-bold text-foreground">{alerts.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Não Lidos</p>
              <p className="text-3xl font-bold text-orange-600">{unreadCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Críticos</p>
              <p className="text-3xl font-bold text-red-600">
                {alerts.filter(a => a.severity === "critical").length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Subscrições Telegram</p>
              <p className="text-3xl font-bold text-blue-600">{subscriptions.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Alertas
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white ml-1">{unreadCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="telegram" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            Telegram
          </TabsTrigger>
        </TabsList>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          {alerts.length === 0 ? (
            <Card className="border-border">
              <CardContent className="py-12 text-center">
                <Bell className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                <p className="text-lg text-muted-foreground">Nenhum alerta</p>
                <p className="text-sm text-muted-foreground">
                  Os alertas aparecerão aqui quando houver eventos importantes
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => {
                const severityConfig = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
                const SeverityIcon = severityConfig.icon;
                const isRead = alert.read_by?.length > 0;
                
                return (
                  <Card 
                    key={alert.id} 
                    className={`border-border ${!isRead ? 'border-l-4 border-l-primary bg-primary/5' : ''}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-full ${severityConfig.color}`}>
                          <SeverityIcon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-foreground">{alert.title}</h3>
                            <Badge variant="outline" className="text-xs">
                              {ALERT_TYPE_LABELS[alert.alert_type] || alert.alert_type}
                            </Badge>
                            {alert.sent_telegram && (
                              <Badge className="bg-blue-500 text-white text-xs">
                                <MessageCircle className="h-3 w-3 mr-1" />
                                Telegram
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{alert.message}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                              {new Date(alert.created_at).toLocaleString("pt-PT")}
                            </span>
                            {!isRead && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleMarkAsRead(alert.id)}
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Marcar como lido
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Telegram Tab */}
        <TabsContent value="telegram">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Subscribe Card */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
                  <MessageCircle className="h-5 w-5" />
                  Subscrever Alertas Telegram
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Como obter o Chat ID:</h4>
                  <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                    <li>Abra o Telegram e procure por <strong>@userinfobot</strong></li>
                    <li>Envie qualquer mensagem ao bot</li>
                    <li>O bot irá responder com o seu Chat ID</li>
                    <li>Copie o número e cole abaixo</li>
                  </ol>
                </div>
                
                <form onSubmit={handleSubscribeTelegram} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Chat ID do Telegram</Label>
                    <Input
                      value={chatId}
                      onChange={(e) => setChatId(e.target.value)}
                      placeholder="Ex: 123456789"
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    <Bell className="h-4 w-4 mr-2" />
                    Ativar Alertas Telegram
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Test Card */}
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
                  <Send className="h-5 w-5" />
                  Testar Conexão
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Envie uma mensagem de teste para verificar se a configuração está correta.
                </p>
                
                <div className="space-y-2">
                  <Label>Chat ID para Teste</Label>
                  <Input
                    value={testChatId}
                    onChange={(e) => setTestChatId(e.target.value)}
                    placeholder="Ex: 123456789"
                  />
                </div>
                <Button onClick={handleTestTelegram} variant="outline" className="w-full">
                  <Send className="h-4 w-4 mr-2" />
                  Enviar Mensagem de Teste
                </Button>
                
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mt-4">
                  <h4 className="font-medium text-amber-800 mb-2">⚠️ Configuração do Bot</h4>
                  <p className="text-sm text-amber-700">
                    Para o envio de mensagens funcionar, o administrador do sistema 
                    precisa configurar o token do bot Telegram na variável de ambiente 
                    <code className="bg-amber-100 px-1 rounded">TELEGRAM_BOT_TOKEN</code>.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Subscriptions List (Admin only) */}
            {subscriptions.length > 0 && (
              <Card className="border-border md:col-span-2">
                <CardHeader>
                  <CardTitle className="font-chivo flex items-center gap-2 text-foreground">
                    <Settings className="h-5 w-5" />
                    Subscrições Ativas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {subscriptions.map((sub) => (
                      <div 
                        key={sub.id}
                        className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-foreground">{sub.user_name}</p>
                          <p className="text-sm text-muted-foreground">Chat ID: {sub.chat_id}</p>
                        </div>
                        <Badge className={sub.active ? "bg-green-500" : "bg-gray-500"}>
                          {sub.active ? "Ativo" : "Inativo"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
